import React, { useState, useEffect } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import AlertsTab from '../alerts/AlertsTab';
import { 
  faDatabase,
  faServer,
  faSync,
  faTachometerAlt,
  faPlus,
  faBolt,
  faGlobe,
  faPlay,
  faStop,
  faChartArea,
  faChevronUp,
  faChartLine,
  faEdit,
  faTrash,
  faExpand,
  faGripVertical,
  faCog
} from '@fortawesome/free-solid-svg-icons';
import { faAws, faGoogle, faMicrosoft, faDocker } from '@fortawesome/free-brands-svg-icons';

const API_BASE_URL = process.env.REACT_APP_API_BASE_URL || 'http://localhost:3001';

interface ConnectedAccount {
  id: string;
  name: string;
  type: 'aws' | 'gcp' | 'github' | 'jenkins';
  status: 'active' | 'inactive' | 'error';
  connectionMethod?: 'iam' | 'cloudformation' | 'oauth' | 'token';
  accountId?: string;
  region?: string;
  lastSync?: string;
  config?: any;
}

interface MonitoringTabProps {
  loading?: boolean;
  connectedAccounts?: ConnectedAccount[];
}

interface DataSource {
  id: string;
  name: string;
  type: 'prometheus' | 'cloudwatch' | 'gcp-monitoring' | 'custom';
  url?: string;
  credentials?: any;
  status: 'connected' | 'error' | 'disconnected';
  lastSync?: string;
  metrics?: AvailableMetric[];
}

interface AvailableMetric {
  name: string;
  description?: string;
  labels?: string[];
  type: 'gauge' | 'counter' | 'histogram';
  unit?: string;
}

interface DashboardPanel {
  id: string;
  title: string;
  dataSource: string;
  metric: string;
  query: string;
  visualization: 'line' | 'bar' | 'gauge' | 'stat';
  size: 'small' | 'medium' | 'large';
  timeRange: '1h' | '6h' | '24h' | '7d' | '30d';
  refreshInterval: number;
  position: { x: number; y: number; w: number; h: number };
}

interface Dashboard {
  id: string;
  name: string;
  description?: string;
  panels: DashboardPanel[];
  createdAt: string;
  updatedAt: string;
  isDefault?: boolean;
}



export const MonitoringTab: React.FC<MonitoringTabProps> = ({ loading: propLoading = false, connectedAccounts = [] }) => {
  const [loading, setLoading] = useState(false);
  const [activeView, setActiveView] = useState<'dashboards' | 'alerts' | 'datasources'>('dashboards');
  const [showFilters, setShowFilters] = useState(false);
  const [autoRefresh, setAutoRefresh] = useState(true);
  
  // Dashboard management state
  const [dashboards, setDashboards] = useState<Dashboard[]>([]);
  const [activeDashboard, setActiveDashboard] = useState<Dashboard | null>(null);
  const [showDashboardEditor, setShowDashboardEditor] = useState(false);
  const [showPanelEditor, setShowPanelEditor] = useState(false);
  const [editingPanel, setEditingPanel] = useState<DashboardPanel | null>(null);
  
  // Data sources state
  const [dataSources, setDataSources] = useState<DataSource[]>([]);
  const [showDataSourceEditor, setShowDataSourceEditor] = useState(false);


  // Initialize data sources from connected accounts
  useEffect(() => {
    const loadDataSources = async () => {
      try {
        // Fetch real connected accounts
        const token = localStorage.getItem('auth_token');
        const response = await fetch(`${API_BASE_URL}/api/accounts`, {
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          }
        });

        if (!response.ok) {
          console.error('Failed to fetch accounts:', response.status);
          setDataSources([]);
          return;
        }

        const accounts = await response.json();
        console.log('Loaded accounts for data sources:', accounts);

        // Transform accounts into data sources
        const sources: DataSource[] = accounts
          .filter((acc: any) => acc.type === 'aws' || acc.type === 'gcp' || acc.type === 'eks')
          .map((acc: any) => {
            const type = acc.type === 'aws' ? 'cloudwatch' : acc.type === 'gcp' ? 'gcp-monitoring' : 'custom';
            const lastSyncDate = acc.lastSync ? new Date(acc.lastSync) : null;
            const lastSyncText = lastSyncDate
              ? `${Math.floor((Date.now() - lastSyncDate.getTime()) / 60000)} minutes ago`
              : 'Never synced';

            const metrics: AvailableMetric[] = [];
            if (acc.type === 'aws') {
              metrics.push(
                { name: 'CPUUtilization', description: 'EC2 CPU utilization', type: 'gauge', unit: 'percent' },
                { name: 'NetworkIn', description: 'Network bytes in', type: 'counter', unit: 'bytes' },
                { name: 'NetworkOut', description: 'Network bytes out', type: 'counter', unit: 'bytes' },
                { name: 'DiskReadBytes', description: 'Disk read bytes', type: 'counter', unit: 'bytes' },
                { name: 'StatusCheckFailed', description: 'Instance status check failures', type: 'gauge', unit: 'count' }
              );
            } else if (acc.type === 'gcp') {
              metrics.push(
                { name: 'compute.googleapis.com/instance/cpu/utilization', description: 'VM CPU utilization', type: 'gauge', unit: 'percent' },
                { name: 'compute.googleapis.com/instance/disk/read_bytes_count', description: 'Disk read bytes', type: 'counter', unit: 'bytes' },
                { name: 'compute.googleapis.com/instance/network/received_bytes_count', description: 'Network bytes received', type: 'counter', unit: 'bytes' }
              );
            } else if (acc.type === 'eks') {
              metrics.push(
                { name: 'pod_cpu_utilization', description: 'Pod CPU usage', type: 'gauge', unit: 'percent' },
                { name: 'pod_memory_utilization', description: 'Pod memory usage', type: 'gauge', unit: 'percent' },
                { name: 'node_count', description: 'Number of nodes', type: 'gauge', unit: 'count' }
              );
            }

            return {
              id: acc.id,
              name: acc.name,
              type: type,
              url: acc.config?.endpoint || undefined,
              status: acc.status === 'active' || acc.status === 'connected' ? 'connected' : 'error',
              lastSync: lastSyncText,
              metrics: metrics
            };
          });

        console.log('Transformed data sources:', sources);
        setDataSources(sources);

        // Set empty dashboards (user can create their own)
        setDashboards([]);
        setActiveDashboard(null);

      } catch (error) {
        console.error('Error loading data sources:', error);
        setDataSources([]);
      }
    };

    loadDataSources();
  }, []);

  const handleRefresh = () => {
    setLoading(true);
    setTimeout(() => {
      setLoading(false);
    }, 1000);
  };



  useEffect(() => {
    console.log('MonitoringTab mounted successfully');
    
    // Auto-refresh every 30 seconds if enabled
    if (autoRefresh) {
      const interval = setInterval(() => {
        console.log('Auto-refreshing monitoring data...');
      }, 30000);
      
      return () => clearInterval(interval);
    }
  }, [autoRefresh]);

  if (loading || propLoading) {
    return (
      <div className="p-3 space-y-3 bg-gray-50 min-h-screen font-mono">
        <div className="animate-pulse">
          <div className="h-6 bg-gray-200 rounded w-1/3 mb-3"></div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-2 mb-3">
            {[1, 2, 3, 4].map(i => (
              <div key={i} className="h-24 bg-gray-200 rounded"></div>
            ))}
          </div>
          <div className="h-96 bg-gray-200 rounded"></div>
        </div>
      </div>
    );
  }

  return (
    <div className="p-3 space-y-3 bg-gray-50 min-h-screen font-mono">
      {/* Simple Header */}
      <div className="bg-white border border-gray-300 rounded p-3">
        <div className="flex items-center justify-between">
          <h1 className="text-base font-bold text-gray-900 flex items-center">
            <FontAwesomeIcon icon={faTachometerAlt} className="text-blue-600 mr-2 text-sm" />
            Resource Monitoring
          </h1>
        <div className="flex items-center space-x-3">
          <div className="flex items-center space-x-2">
            <div className={`w-2 h-2 rounded-full ${autoRefresh ? 'bg-green-400 animate-pulse' : 'bg-gray-400'}`}></div>
            <span className="text-xs text-gray-600">{autoRefresh ? 'Live' : 'Paused'}</span>
          </div>
          <button
            onClick={() => setAutoRefresh(!autoRefresh)}
            className="px-3 py-1.5 text-sm bg-white border border-gray-300 text-gray-700 rounded hover:bg-gray-50 font-medium"
          >
            <FontAwesomeIcon icon={autoRefresh ? faStop : faPlay} className="mr-1 text-xs" />
            {autoRefresh ? 'Pause' : 'Resume'}
          </button>
        </div>
        </div>
      </div>

      {/* Navigation Tabs */}
      <div className="bg-white border border-gray-300 rounded">
        <div className="flex space-x-1 p-1 overflow-x-auto">
          <button
            onClick={() => setActiveView('dashboards')}
            className={`px-3 py-1.5 text-xs font-medium rounded whitespace-nowrap ${
              activeView === 'dashboards'
                ? 'bg-blue-100 text-blue-700'
                : 'text-gray-500 hover:text-gray-700 hover:bg-gray-100'
            }`}
          >
            <FontAwesomeIcon icon={faTachometerAlt} className="mr-1" />
            Dashboards
          </button>
          <button
            onClick={() => setActiveView('alerts')}
            className={`px-3 py-1.5 text-xs font-medium rounded whitespace-nowrap ${
              activeView === 'alerts'
                ? 'bg-blue-100 text-blue-700'
                : 'text-gray-500 hover:text-gray-700 hover:bg-gray-100'
            }`}
          >
            <FontAwesomeIcon icon={faBolt} className="mr-1" />
            Alerts
          </button>
          <button
            onClick={() => setActiveView('datasources')}
            className={`px-3 py-1.5 text-xs font-medium rounded whitespace-nowrap ${
              activeView === 'datasources'
                ? 'bg-blue-100 text-blue-700'
                : 'text-gray-500 hover:text-gray-700 hover:bg-gray-100'
            }`}
          >
            <FontAwesomeIcon icon={faPlus} className="mr-1" />
            Data Sources
          </button>
        </div>
      </div>

      {/* Content based on active view */}
      <div className="space-y-4">
        {activeView === 'dashboards' && (
          <div className="space-y-4">
            {/* Dashboard Header - Only show if there are dashboards */}
            {dashboards.length > 0 && (
              <div className="bg-white border border-gray-200 rounded-lg p-4">
                <div className="flex items-center justify-between">
                  <div className="flex items-center space-x-4">
                    <div>
                      <select
                        value={activeDashboard?.id || ''}
                        onChange={(e) => setActiveDashboard(dashboards.find(d => d.id === e.target.value) || null)}
                        className="text-sm font-semibold bg-transparent border-none focus:outline-none cursor-pointer"
                      >
                        {dashboards.map(dashboard => (
                          <option key={dashboard.id} value={dashboard.id}>{dashboard.name}</option>
                        ))}
                      </select>
                      <p className="text-xs text-gray-600">{activeDashboard?.description}</p>
                    </div>
                    {activeDashboard?.isDefault && (
                      <span className="px-2 py-1 bg-blue-100 text-blue-700 text-xs rounded">Default</span>
                    )}
                  </div>
                  <div className="flex items-center space-x-3">
                    <div className="flex items-center space-x-2">
                      <label className="text-xs text-gray-600">Time range:</label>
                      <select className="text-xs border border-gray-300 rounded px-2 py-1">
                        <option>Last 1 hour</option>
                        <option>Last 6 hours</option>
                        <option selected>Last 24 hours</option>
                        <option>Last 7 days</option>
                        <option>Last 30 days</option>
                      </select>
                    </div>
                    <button
                      onClick={() => setShowDashboardEditor(true)}
                      className="px-3 py-1.5 bg-gray-100 text-gray-700 rounded text-xs hover:bg-gray-200"
                    >
                      <FontAwesomeIcon icon={faEdit} className="mr-1" />
                      Edit
                    </button>
                    <button
                      onClick={() => setShowDashboardEditor(true)}
                      className="px-3 py-1.5 bg-blue-600 text-white rounded text-xs hover:bg-blue-700"
                    >
                      <FontAwesomeIcon icon={faPlus} className="mr-1" />
                      New Dashboard
                    </button>
                  </div>
                </div>
              </div>
            )}

            {/* Dashboard Panels Grid */}
            {!activeDashboard && dashboards.length === 0 && (
              <div className="bg-white border border-gray-200 rounded-lg p-12">
                <div className="text-center">
                  <FontAwesomeIcon icon={faTachometerAlt} className="text-5xl text-gray-300 mb-4" />
                  <h3 className="text-sm font-medium text-gray-900 mb-2">No Dashboards Yet</h3>
                  <p className="text-gray-600 mb-6">Create your first dashboard to visualize metrics from your data sources</p>
                  <button
                    onClick={() => setShowDashboardEditor(true)}
                    className="px-6 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors inline-flex items-center"
                  >
                    <FontAwesomeIcon icon={faPlus} className="mr-2" />
                    Create Dashboard
                  </button>
                </div>
              </div>
            )}

            {activeDashboard && (
              <div className="grid grid-cols-12 gap-4">
                {activeDashboard.panels.map((panel) => (
                  <div 
                    key={panel.id}
                    className="bg-white border border-gray-200 rounded-lg hover:shadow-lg transition-shadow group"
                    style={{
                      gridColumn: `span ${panel.position.w}`,
                      gridRow: `span ${panel.position.h}`
                    }}
                  >
                    {/* Panel Header */}
                    <div className="p-3 border-b border-gray-100 flex items-center justify-between">
                      <div className="flex items-center space-x-2">
                        <FontAwesomeIcon icon={faGripVertical} className="text-gray-400 opacity-0 group-hover:opacity-100 cursor-move" />
                        <div>
                          <h4 className="text-xs font-medium text-gray-900">{panel.title}</h4>
                          <div className="text-xs text-gray-500">
                            {dataSources.find(ds => ds.id === panel.dataSource)?.name} • {panel.timeRange}
                          </div>
                        </div>
                      </div>
                      <div className="flex items-center space-x-1 opacity-0 group-hover:opacity-100">
                        <button 
                          onClick={() => {setEditingPanel(panel); setShowPanelEditor(true);}}
                          className="p-1 text-gray-400 hover:text-gray-600 text-xs"
                        >
                          <FontAwesomeIcon icon={faEdit} />
                        </button>
                        <button 
                          className="p-1 text-gray-400 hover:text-gray-600 text-xs"
                        >
                          <FontAwesomeIcon icon={faExpand} />
                        </button>
                        <button 
                          className="p-1 text-gray-400 hover:text-red-600 text-xs"
                        >
                          <FontAwesomeIcon icon={faTrash} />
                        </button>
                      </div>
                    </div>

                    {/* AWS-Style Line Chart */}
                    <div className="p-4">
                      <div className="relative">
                        {/* Chart Container */}
                        <div className="h-32 bg-gray-50 rounded border relative overflow-hidden cursor-crosshair">
                          {/* Grid Lines */}
                          <svg className="absolute inset-0 w-full h-full">
                            {/* Horizontal grid lines */}
                            {[0.25, 0.5, 0.75].map((ratio) => (
                              <line
                                key={ratio}
                                x1="0"
                                y1={`${ratio * 100}%`}
                                x2="100%"
                                y2={`${ratio * 100}%`}
                                stroke="#e5e7eb"
                                strokeWidth="1"
                                strokeDasharray="2,2"
                              />
                            ))}
                            {/* Vertical grid lines */}
                            {[0.2, 0.4, 0.6, 0.8].map((ratio) => (
                              <line
                                key={ratio}
                                x1={`${ratio * 100}%`}
                                y1="0"
                                x2={`${ratio * 100}%`}
                                y2="100%"
                                stroke="#e5e7eb"
                                strokeWidth="1"
                                strokeDasharray="2,2"
                              />
                            ))}
                            
                            {/* AWS-style line chart */}
                            <polyline
                              fill="none"
                              stroke="#3b82f6"
                              strokeWidth="2"
                              points={
                                Array.from({ length: 50 }, (_, i) => {
                                  const x = (i / 49) * 100;
                                  const baseValue = panel.metric === 'CPUUtilization' ? 45 : 
                                                  panel.metric === 'node_memory_MemAvailable_bytes' ? 65 : 35;
                                  const noise = (Math.sin(i * 0.3) + Math.sin(i * 0.7) * 0.5) * 15 + (Math.random() - 0.5) * 10;
                                  const y = 100 - Math.max(5, Math.min(95, baseValue + noise));
                                  return `${x},${y}`;
                                }).join(' ')
                              }
                            />
                            
                            {/* Data points */}
                            {Array.from({ length: 10 }, (_, i) => {
                              const x = (i / 9) * 100;
                              const baseValue = panel.metric === 'CPUUtilization' ? 45 : 
                                              panel.metric === 'node_memory_MemAvailable_bytes' ? 65 : 35;
                              const noise = (Math.sin(i * 1.5) + Math.sin(i * 2.1) * 0.5) * 15;
                              const y = 100 - Math.max(5, Math.min(95, baseValue + noise));
                              return (
                                <circle
                                  key={i}
                                  cx={`${x}%`}
                                  cy={`${y}%`}
                                  r="3"
                                  fill="#3b82f6"
                                  className="opacity-0 hover:opacity-100 cursor-pointer"
                                />
                              );
                            })}
                          </svg>
                          
                          {/* Y-axis labels */}
                          <div className="absolute left-1 top-1 text-xs text-gray-500">100</div>
                          <div className="absolute left-1 top-1/2 text-xs text-gray-500 transform -translate-y-1/2">50</div>
                          <div className="absolute left-1 bottom-1 text-xs text-gray-500">0</div>
                        </div>
                        
                        {/* Current Value Display */}
                        <div className="flex items-center justify-between mt-2">
                          <div className="text-xs text-gray-500">
                            Query: <code className="bg-gray-100 px-1 rounded text-xs">{panel.query}</code>
                          </div>
                          <div className="text-xs font-medium text-gray-900">
                            {panel.metric === 'CPUUtilization' ? '47.3%' :
                             panel.metric === 'node_memory_MemAvailable_bytes' ? '68.2%' : '2.4k/s'}
                            <span className="ml-2 text-xs text-green-600">
                              <FontAwesomeIcon icon={faChevronUp} />
                            </span>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                ))}
                
                {/* Add Panel Button */}
                <div 
                  className="col-span-6 h-48 border-2 border-dashed border-gray-300 rounded-lg flex items-center justify-center cursor-pointer hover:border-blue-400 hover:bg-blue-50 transition-colors"
                  onClick={() => setShowPanelEditor(true)}
                >
                  <div className="text-center">
                    <FontAwesomeIcon icon={faPlus} className="text-2xl text-gray-400 mb-2" />
                    <p className="text-xs text-gray-600">Add Panel</p>
                  </div>
                </div>
              </div>
            )}
          </div>
        )}

        {/* Alerts Tab Content */}
        {activeView === 'alerts' && (
          <AlertsTab connectedAccounts={connectedAccounts as any} />
        )}

        {/* Data Sources Tab Content */}
        {activeView === 'datasources' && (
          <div className="space-y-4">
            {/* Data Sources Header */}
            <div className="bg-white border border-gray-200 rounded-lg p-4">
              <div className="flex items-center justify-between">
                <div>
                  <h3 className="text-sm font-semibold text-gray-900">Data Sources</h3>
                  <p className="text-xs text-gray-600 mt-1">Manage connections to monitoring systems and metrics providers</p>
                </div>
                <button 
                  onClick={() => setShowDataSourceEditor(true)}
                  className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
                >
                  <FontAwesomeIcon icon={faPlus} className="mr-2" />
                  Add Data Source
                </button>
              </div>
            </div>

            {/* Connected Data Sources */}
            <div className="bg-white border border-gray-200 rounded-lg">
              <div className="p-4 border-b border-gray-200">
                <h4 className="font-medium text-gray-900">Connected Sources ({dataSources.length})</h4>
              </div>
              {dataSources.length === 0 ? (
                <div className="p-12 text-center">
                  <FontAwesomeIcon icon={faDatabase} className="text-5xl text-gray-300 mb-4" />
                  <h3 className="text-sm font-medium text-gray-900 mb-2">No Data Sources Connected</h3>
                  <p className="text-xs text-gray-600 mb-6">Connect your AWS or GCP accounts to start monitoring metrics and alerts</p>
                  <p className="text-xs text-gray-500">Data sources are automatically created from your connected accounts in the Accounts tab</p>
                </div>
              ) : (
                <div className="divide-y divide-gray-200">
                  {dataSources.map((source) => (
                  <div key={source.id} className="p-4 hover:bg-gray-50">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center space-x-4">
                        <div className={`w-3 h-3 rounded-full ${
                          source.status === 'connected' ? 'bg-green-500' :
                          source.status === 'error' ? 'bg-red-500' : 'bg-gray-400'
                        }`}></div>
                        <div className="flex items-center space-x-3">
                          <div className={`w-8 h-8 rounded-lg flex items-center justify-center ${
                            source.type === 'prometheus' ? 'bg-orange-100' :
                            source.type === 'cloudwatch' ? 'bg-orange-100' :
                            source.type === 'gcp-monitoring' ? 'bg-blue-100' : 'bg-gray-100'
                          }`}>
                            <FontAwesomeIcon 
                              icon={
                                source.type === 'prometheus' ? faChartLine :
                                source.type === 'cloudwatch' ? faAws :
                                source.type === 'gcp-monitoring' ? faGoogle : faServer
                              } 
                              className={`text-sm ${
                                source.type === 'prometheus' ? 'text-orange-600' :
                                source.type === 'cloudwatch' ? 'text-orange-600' :
                                source.type === 'gcp-monitoring' ? 'text-blue-600' : 'text-gray-600'
                              }`}
                            />
                          </div>
                          <div>
                            <h5 className="text-xs font-medium text-gray-900">{source.name}</h5>
                            <div className="text-[10px] text-gray-600">
                              {source.type.toUpperCase()}
                              {source.url && <span> • {source.url}</span>}
                              <span> • {source.metrics?.length || 0} metrics</span>
                            </div>
                          </div>
                        </div>
                      </div>
                      <div className="flex items-center space-x-3">
                        <span className="text-xs text-gray-500">
                          Last sync: {source.lastSync}
                        </span>
                        <div className="flex items-center space-x-1">
                          <button 
                            className="p-1 text-gray-400 hover:text-blue-600"
                            title="Test Connection"
                          >
                            <FontAwesomeIcon icon={faSync} />
                          </button>
                          <button 
                            className="p-1 text-gray-400 hover:text-gray-600"
                            title="Configure"
                          >
                            <FontAwesomeIcon icon={faCog} />
                          </button>
                          <button 
                            className="p-1 text-gray-400 hover:text-red-600"
                            title="Delete"
                          >
                            <FontAwesomeIcon icon={faTrash} />
                          </button>
                        </div>
                      </div>
                    </div>
                    
                    {/* Available Metrics Preview */}
                    <div className="mt-3 ml-7">
                      <div className="text-xs text-gray-500 mb-2">Available Metrics:</div>
                      <div className="flex flex-wrap gap-2">
                        {source.metrics?.slice(0, 5).map((metric) => (
                          <span key={metric.name} className="px-2 py-1 bg-gray-100 text-gray-700 rounded text-xs">
                            {metric.name}
                          </span>
                        ))}
                        {source.metrics && source.metrics.length > 5 && (
                          <span className="px-2 py-1 bg-gray-50 text-gray-500 rounded text-xs">
                            +{source.metrics.length - 5} more
                          </span>
                        )}
                      </div>
                    </div>
                  </div>
                ))}
                </div>
              )}
            </div>

            {/* Data Source Templates */}
            <div className="bg-white border border-gray-200 rounded-lg p-4">
              <h4 className="font-medium text-gray-900 mb-4">Add New Data Source</h4>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                {[
                  {
                    name: 'Prometheus',
                    description: 'PromQL queries and metrics collection',
                    icon: faChartLine,
                    color: 'orange',
                    popular: true
                  },
                  {
                    name: 'AWS CloudWatch',
                    description: 'AWS metrics and logs',
                    icon: faAws,
                    color: 'orange',
                    popular: true
                  },
                  {
                    name: 'GCP Monitoring',
                    description: 'Google Cloud monitoring data',
                    icon: faGoogle,
                    color: 'blue',
                    popular: true
                  },
                  {
                    name: 'Custom HTTP',
                    description: 'Generic HTTP metrics endpoint',
                    icon: faGlobe,
                    color: 'blue',
                    popular: false
                  },
                  {
                    name: 'InfluxDB',
                    description: 'Time-series database queries',
                    icon: faDatabase,
                    color: 'purple',
                    popular: false
                  },
                  {
                    name: 'Grafana',
                    description: 'Import from existing Grafana',
                    icon: faChartArea,
                    color: 'orange',
                    popular: false
                  }
                ].map((template, idx) => (
                  <div 
                    key={idx} 
                    className="relative border border-gray-200 rounded-lg p-4 hover:shadow-md transition-shadow cursor-pointer hover:border-blue-300"
                    onClick={() => setShowDataSourceEditor(true)}
                  >
                    {template.popular && (
                      <span className="absolute -top-2 -right-2 bg-blue-500 text-white text-xs px-2 py-1 rounded-full">
                        Popular
                      </span>
                    )}
                    <div className="flex items-center space-x-3 mb-3">
                      <div className={`w-8 h-8 rounded-lg flex items-center justify-center ${
                        template.color === 'orange' ? 'bg-orange-100' :
                        template.color === 'blue' ? 'bg-blue-100' :
                        template.color === 'purple' ? 'bg-purple-100' : 'bg-gray-100'
                      }`}>
                        <FontAwesomeIcon icon={template.icon} className={`text-${template.color}-600`} />
                      </div>
                      <h5 className="font-medium text-gray-900">{template.name}</h5>
                    </div>
                    <p className="text-xs text-gray-600">{template.description}</p>
                  </div>
                ))}
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default MonitoringTab;